import React, { useEffect, useContext, useState, useMemo } from "react";

import UserContext from "../../components/User_Context.js";
import * as menu from "../../assets/menus/menu.js";
import * as breadcrumb from "../../components/ui/breadcrumb.js";
import * as api from "../../api/apiConnector.js";
import { useTranslation } from "react-i18next";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { LisLabels } from "../_label/lis_labels/lis_labels.js";
import LisTableHeader from "../../components/lis/LisTableHeader.js";
import LisMainTable from "../../components/lis/LisMainTable.js";


const SourceDataTableScreen = () => {
  const { currentUser } = useContext(UserContext);

  const { t } = useTranslation();
  const MySwal = withReactContent(Swal);
  const [tableData, setTableData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    fromDate: "",
    toDate: "",
    businessUnit: "",
    testDescription: "",
    microbeName: "",
    antibioticName: "",
    gender: "",
    error: null,
  });

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    businessUnit: "",
    testDescription: "",
    microbeName: "",
    antibioticName: "",
    gender: "",
    error: null,
  });

  const errorOptions = useMemo(
    () => [
      { label: "True", value: "true" },
      { label: "False", value: "false" },
    ],
    []
  );
  // Initialize i18n labels (reuse lis labels for now)
  const i18n = menu.getI18n();
  i18n.addResourceBundle("it", "lisLabels", LisLabels.it, true, true);
  i18n.addResourceBundle("en", "lisLabels", LisLabels.en, true, true);

  useEffect(() => {
    // Set breadcrumb
    const path = [
      { link: "/dashboard", name: "Home" },
      { link: "/admin", name: "Admin" },
      {
        link: "/admin/source_data_table",
        name: "LIS Source Data Table",
      },
    ];
    breadcrumb.set(path);

    // Write menu to make it visible
    menu.write({ user: currentUser() });

    // Initial load
    fetchTableData({ page: 1, pageSizeParam: 100, ...activeFilters });
  }, []);

  const fetchTableData = async (params = {}) => {
    const {
      page = 1,
      pageSizeParam = 100,
      fromDate = "",
      toDate = "",
      ...filters
    } = params || {};

    try {
      setIsLoading(true);
      const effectivePageSize = pageSizeParam ?? pagination?.pageSize ?? 10;
      const parsedFromDate = new Date(fromDate);
      const parsedToDate = new Date(toDate);
      const response = await api.sendMessage(
        `admin/lis/getLisSourceDataTable`,
        {
          user: currentUser(),
          body: {
            page,
            pageSize: effectivePageSize,
            fromDate: parsedFromDate,
            toDate: parsedToDate,
            ...filters,
          },
        }
      );
      if (response.success) {
        setTableData(response.data);
        setHeaders(response.headers || []);
        setPagination(response.pagination || null);
      }
    } catch (error) {
      console.error("Error fetching table data:", error);
      MySwal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch table data",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (maybeFilters) => {
    // Use current local filters if none are passed by the header
    const raw = maybeFilters ?? filters;
    // Normalize error value and key
    const normalized = (() => {
      const { errorFilter, error, ...rest } = raw || {};
      let effectiveError = error ?? errorFilter;
      if (effectiveError === "" || effectiveError === null || effectiveError === undefined) {
        effectiveError = undefined;
      } else if (typeof effectiveError === "string") {
        if (effectiveError.toLowerCase() === "true") effectiveError = true;
        else if (effectiveError.toLowerCase() === "false") effectiveError = false;
      }
      return { ...rest, ...(effectiveError !== undefined ? { error: effectiveError } : {}) };
    })();
    setActiveFilters(normalized);
    fetchTableData({
      page: 1,
      pageSizeParam: pagination?.pageSize ?? 100,
      ...normalized,
    });
  };

  // Localize header labels through i18n with safe fallbacks
  const localizeHeaderLabel = (header) => {
    const headerKey = typeof header === "object" ? header?.key : String(header);
    const fallbackLabel =
      typeof header === "object" ? header?.label ?? headerKey : headerKey;
    if (!headerKey || headerKey === "_ERRORS_SUMMARY") return fallbackLabel;
    // Map backend/DB column names to i18n keys where needed
    const predefinedKeyMap = {
      ID_RICHIESTA: "requestId",
      DATA_ACCETTAZIONE: "admittanceDate",
      SESSO: "gender",
      DESCRIZIONE_REPARTO: "department",
      DESCRIZIONE_ANALISI: "testDescription",
      DESCRIZIONE_MATERIALE: "materialDescription",
      DESCRIZIONE_PROVENIENZA: "provenanceDescription",
      DESCRIZIONE_MICRO: "microDescription",
      DESCRIZIONE_ANTIBIO: "antibioticDescription",
    };
    const i18nKey = predefinedKeyMap[headerKey] ?? headerKey;
    return t(i18nKey, { ns: "lisLabels", defaultValue: fallbackLabel });
  };

  // Derive effective headers (fallback to data keys, hide ID_* and *_ERROR, add error summary column)
  const effectiveHeaders = useMemo(() => {
    let base = [];
    if (Array.isArray(headers) && headers.length > 0) {
      base = headers
        .map((h) => (typeof h === "object" ? h : { key: String(h), label: String(h) }))
        .filter((h) => {
          const key = h.key ?? "";
          return !key.startsWith("ID_") && !key.endsWith("_ERROR");
        });
    } else if (Array.isArray(tableData) && tableData.length > 0) {
      const keys = Object.keys(tableData[0] || {});
      base = keys
        .filter((k) => !k.startsWith("ID_") && !k.endsWith("_ERROR"))
        .map((k) => ({ key: k, label: k }));
    }

    // Explicitly include ID_RICHIESTA column if present in headers or data
    let idRichiestaHeader = null;
    if (Array.isArray(headers) && headers.length > 0) {
      const found = headers.find((h) =>
        (typeof h === "object" ? h.key : String(h)) === "ID_RICHIESTA"
      );
      if (found) {
        idRichiestaHeader =
          typeof found === "object"
            ? { key: "ID_RICHIESTA",   label: t("requestId", { ns: "lisLabels" }), }
            : { key: "ID_RICHIESTA",  label: t("requestId", { ns: "lisLabels" }), };
      }
    }
    if (
      !idRichiestaHeader &&
      Array.isArray(tableData) &&
      tableData.length > 0 &&
      Object.prototype.hasOwnProperty.call(tableData[0] || {}, "ID_RICHIESTA")
    ) {
      idRichiestaHeader = { key: "ID_RICHIESTA", label: t("requestId", { ns: "lisLabels" }), };
    }
    if (idRichiestaHeader) {
      const alreadyIncluded = base.some((h) => (h?.key ?? "") === "ID_RICHIESTA");
      if (!alreadyIncluded) {
        base.unshift(idRichiestaHeader);
      }
    }

    // Localize all visible header labels
    const localizedBase = base.map((h) => ({
      ...h,
      label: localizeHeaderLabel(h),
    }));

    if (base.length > 0) {
      const withErrorCol = [...localizedBase];
      withErrorCol.splice(1, 0, { key: "_ERRORS_SUMMARY", label: t("error", { ns: "lisLabels" }) });
      return withErrorCol;
    }
    return localizedBase;
  }, [headers, tableData, t]);

  const getErrorSummary = (row) => {
    if (!row) return [];
    const errorMap = {
      ID_REPARTO_ERROR: "REP",
      ID_MATERIALE_ERROR: "MAT",
      ID_PROVENIENZA_ERROR: "PROV",
      ID_MICRO_ERROR: "MICRO",
      ID_ANTIBIOTICO_ERROR: "ANTIB",
      ID_ANALISI_ERROR: "ANALI",
    };
    const errors = [];
    Object.entries(errorMap).forEach(([k, code]) => {
      if (row[k] === 1 || row[k] === true) {
        errors.push(code);
      }
    });
    return errors;
  };

  const formatCellValue = (key, value) => {
    if (value === undefined || value === null) return "-";
    if (key === "DATA_ACCETTAZIONE") {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        try {
          return d.toLocaleDateString(undefined, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
        } catch {
          return d.toLocaleDateString();
        }
      }
    }
    return String(value);
  };

  return (
    <>
      <div className="page-titles">
        <ol className="breadcrumb"></ol>
      </div>
      <div className="container-fluid">
        <div className="card-body p-0 bg-white">
          <div className="table-responsive active-projects task-table">
            <div className="tbl-caption h-auto">
              <div className="row">
                <div className="col-12">
                  <div className="col-12">
                    <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-4">
                      <div>
                        <h3 className="mb-0">
                          <i className="fas fa-database me-2"></i>
                          {t("LIS Source Data Table", { ns: "lisLabels" })}
                        </h3>
                        <p className="text-muted mb-0">
                          {t("Manage source data table records", {
                            ns: "lisLabels",
                          })}
                        </p>
                      </div>
                      <div
                        className="alert alert-info mb-0 py-2 px-3"
                        style={{ fontSize: "0.85rem" }}
                      >
                        <div className="d-flex align-items-center flex-wrap gap-1">
                          <strong className="me-2">
                            <i className="fas fa-info-circle me-1"></i>
                            {t("errorLegendTitle", { ns: "lisLabels" })}:
                          </strong>
                          <span className="text-muted">
                            <strong>REP</strong>=Reparto · <strong>MAT</strong>=Materiale · <strong>PROV</strong>=Provenienza · <strong>MICRO</strong>=Microrganismo · <strong>ANTIB</strong>=Antibiotico · <strong>ANALI</strong>=Analisi
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <LisTableHeader
                  filtersConfig={[
                    {
                      key: "fromDate",
                      label: t("admittanceFromDate", { ns: "lisLabels" }),
                      type: "date",
                    },
                    {
                      key: "toDate",
                      label: t("admittanceToDate", { ns: "lisLabels" }),
                      type: "date",
                    },
                    {
                      key: "gender",
                      label: t("gender", { ns: "lisLabels" }),
                      type: "select",
                      options: [
                        {
                          value: "M",
                          label: t("male", { ns: "lisLabels" }),
                        },
                        {
                          value: "F",
                          label: t("female", { ns: "lisLabels" }),
                        },
                      ],
                      placeholder: t("gender", { ns: "lisLabels" }),
                    },
                    {
                      key: "businessUnit",
                      label: t("businessUnit", { ns: "lisLabels" }),
                      type: "text",
                      placeholder: t("businessUnit", { ns: "lisLabels" }),
                    },
                    {
                      key: "testDescription",
                      label: t("testDescription", { ns: "lisLabels" }),
                      type: "text",
                      placeholder: t("testDescription", { ns: "lisLabels" }),
                    },
                    {
                      key: "microbeName",
                      label: t("microbeName", { ns: "lisLabels" }),
                      type: "text",
                      placeholder: t("microbeName", { ns: "lisLabels" }),
                    },
                    {
                      key: "antibioticName",
                      label: t("antibioticName", { ns: "lisLabels" }),
                      type: "text",
                      placeholder: t("antibioticName", { ns: "lisLabels" }),
                    },
                    {
                      key: "error",
                      label: t("error", { ns: "lisLabels" }),
                      type: "select",
                      options: [
                        { label: t("hasErrors", { ns: "lisLabels" }), value: "true" },
                        { label: t("noErrors", { ns: "lisLabels" }), value: "false" },
                      ],
                      placeholder: t("error", { ns: "lisLabels" }),
                    },
                  ]}
                  values={filters}
                  onChange={(key, value) =>
                    setFilters({ ...filters, [key]: value })
                  }
                  onSearch={() => handleSearch()}
                  onReset={() =>
                    setFilters({
                      fromDate: "",
                      toDate: "",
                      businessUnit: "",
                      testDescription: "",
                      microbeName: "",
                      antibioticName: "",
                      error: null,
                    })
                  }
                  isLoading={isLoading}
                />
                <div className="card">
                  <div className="card-body">
                    <LisMainTable
                      headers={effectiveHeaders}
                      rows={tableData}
                      isLoading={isLoading}
                      pagination={pagination}
                      maxHeight={450}
                      onPageChange={(p) =>
                        fetchTableData({
                          page: p,
                          pageSizeParam: pagination?.pageSize ?? 100,
                          ...activeFilters,
                        })
                      }
                      getRowKey={(row, idx) => row?.id ?? row?.ID ?? idx}
                      renderCell={({ row, header, value }) => {
                        if (header?.key === "_ERRORS_SUMMARY") {
                          const summary = getErrorSummary(row);
                          const hasErrors = summary.length > 0;
                          const title = hasErrors
                            ? `Errori di mappatura: ${summary.join(", ")}`
                            : "Nessun errore - Tutti i campi mappati correttamente";
                          return <span title={title}>{hasErrors ? "🔴" : "🟢"}</span>;
                        }
                        return formatCellValue(header?.key, value);
                      }}
                    />
                  </div>
                </div>

             
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SourceDataTableScreen;
